import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import { TaxWithUsage } from '../types/tax.type';
import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { ADMIN_TAXES_API_PATHS } from '../constants/taxes-api-paths.constants';

type UseAdminGetTaxesResult = {
  isLoading: boolean;
  isRefetching: boolean;
  taxes: TaxWithUsage[];
  totalCount: number;
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetTaxesResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  taxes: [],
  totalCount: 0,
};

const QUERY_PARAMS = { page: 1, limit: 1000, sortBy: 'name', sortDirection: 'asc' };

export function useAdminGetTaxes(): UseAdminGetTaxesResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<{ totalCount: number; taxes: TaxWithUsage[] }>(
        ADMIN_TAXES_API_PATHS.GET_TAXES,
        {
          params: QUERY_PARAMS,
        }
      );

      setResult({ isLoading: false, isRefetching: false, taxes: response.taxes, totalCount: response.totalCount });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, 'Une erreur est survenue lors de la récupération des taxes'),
        { variant: 'error' }
      );
      setResult({ isLoading: false, isRefetching: false, taxes: [], totalCount: 0 });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
